import { useEffect, useState } from "react";
import { differenceInCalendarDays, parseISO, addDays, format } from "date-fns";

export const FLOWS = ["Spotting", "Light", "Medium", "Heavy"];
export const MOODS = ["Calm", "Happy", "Energised", "Anxious", "Irritable", "Sad", "Tired"];
export const SYMPTOM_LIST = ["Cramps", "Bloating", "Headache", "Breast tenderness", "Acne", "Back pain", "Cravings", "Fatigue", "Nausea", "Insomnia", "Discharge", "Low libido"];

const KEY = "lunelle-cycle";
const EVT = "lunelle-cycle-change";
const DEFAULTS = { starts: [], cycleLength: 28, periodLength: 5, logs: {}, pregnant: false, lmp: null };

const load = () => {
  try {
    const s = JSON.parse(window.localStorage.getItem(KEY) || "{}");
    return { ...DEFAULTS, ...(s && typeof s === "object" ? s : {}), starts: Array.isArray(s?.starts) ? s.starts : [],
      logs: s?.logs && typeof s.logs === "object" ? s.logs : {} };
  } catch {
    return DEFAULTS;
  }
};

export const loadCycle = load;

// Replace the browser copy (e.g. from the account) and notify every useCycle hook
export const saveCycle = (c) => {
  window.localStorage.setItem(KEY, JSON.stringify({ ...DEFAULTS, ...c }));
  window.dispatchEvent(new Event(EVT));
};
export const clearCycle = () => saveCycle(DEFAULTS);
export const CYCLE_EVENT = EVT;

export const hasCycleData = (c) => Boolean(c?.starts?.length || Object.keys(c?.logs ?? {}).length || c?.lmp);

// Account copy + browser copy: union of period starts and day logs (browser wins on the same day, symptoms combined)
export const mergeCycles = (account, local) => {
  const logs = { ...(account.logs ?? {}) };
  Object.entries(local.logs ?? {}).forEach(([iso, l]) => {
    const a = logs[iso] ?? {};
    logs[iso] = { ...a, ...Object.fromEntries(Object.entries(l).filter(([, v]) => v)), symptoms: [...new Set([...(a.symptoms ?? []), ...(l.symptoms ?? [])])] };
  });
  return { ...DEFAULTS, ...account, starts: [...new Set([...(account.starts ?? []), ...(local.starts ?? [])])].sort().slice(-12), logs,
    lmp: account.lmp || local.lmp || null };
};

export const useCycle = () => {
  const [cycle, setCycle] = useState(load);
  useEffect(() => {
    const s = JSON.stringify(cycle);
    if (window.localStorage.getItem(KEY) === s) return;
    window.localStorage.setItem(KEY, s);
    window.dispatchEvent(new Event(EVT));
  }, [cycle]);
  useEffect(() => {
    const sync = () => setCycle((prev) => {
      const next = load();
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, []);
  const toggleStart = (iso) => setCycle((c) => ({
    ...c, starts: c.starts.includes(iso) ? c.starts.filter((d) => d !== iso) : [...c.starts, iso].sort().slice(-12),
  }));
  const set = (k, v) => setCycle((c) => ({ ...c, [k]: v }));
  const setLog = (iso, patch) => setCycle((c) => {
    const next = { ...(c.logs[iso] ?? {}), ...patch };
    const empty = !next.flow && !next.mood && !(next.symptoms ?? []).length;
    const logs = { ...c.logs };
    if (empty) delete logs[iso];
    else logs[iso] = next;
    return { ...c, logs };
  });
  const reset = () => setCycle(DEFAULTS);
  return { cycle, toggleStart, set, setLog, reset };
};

export const avgCycleLength = (c) => {
  const gaps = c.starts.slice(1).map((d, i) => differenceInCalendarDays(parseISO(d), parseISO(c.starts[i]))).filter((g) => g >= 15 && g <= 60);
  return gaps.length ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : Number(c.cycleLength) || 28;
};

// Learns period length from flow logged after each start (Spotting doesn't count, one missed day is bridged).
// Only finished periods count: the day after the last flow day must be in the past.
const BLEED = ["Light", "Medium", "Heavy"];
export const learnedPeriodLength = (c) => {
  const bleeds = (d) => BLEED.includes(c.logs?.[format(d, "yyyy-MM-dd")]?.flow);
  const lengths = c.starts.map((iso) => {
    const start = parseISO(iso);
    let last = 0;
    for (let i = 1; i < 15; i += 1) {
      if (bleeds(addDays(start, i))) last = i;
      else if (i - last > 1) break;
    }
    const finished = differenceInCalendarDays(new Date(), addDays(start, last + 1)) > 0;
    return last >= 1 && finished ? last + 1 : null;
  }).filter(Boolean).slice(-6);
  return lengths.length ? { avg: Math.round(lengths.reduce((a, b) => a + b, 0) / lengths.length), count: lengths.length } : null;
};

export const periodLengthFor = (c) => learnedPeriodLength(c)?.avg ?? (Number(c.periodLength) || 5);

export const phaseForDay = (day, len, periodLen) => {
  const ov = len - 14;
  if (day <= periodLen) return "menstrual";
  if (day >= ov - 1 && day <= ov + 1) return "ovulatory";
  if (day < ov - 1) return "follicular";
  return "luteal";
};

// Cycle day (1-based) for a date, projected forward from the latest logged start before it
export const infoForDate = (c, date) => {
  const before = c.starts.filter((d) => parseISO(d) <= date);
  if (!before.length) return null;
  const len = avgCycleLength(c);
  const diff = differenceInCalendarDays(date, parseISO(before[before.length - 1]));
  const day = (diff % len) + 1;
  const ov = len - 14;
  return { day, len, phase: phaseForDay(day, len, periodLengthFor(c)), predicted: diff >= len, fertile: day >= ov - 5 && day <= ov + 1 };
};

// Days overdue vs. expected next period (2-day grace), from the latest logged start
export const lateBy = (c) => {
  if (!c.starts.length) return 0;
  const diff = differenceInCalendarDays(new Date(), parseISO(c.starts[c.starts.length - 1]));
  const over = diff - avgCycleLength(c) + 1;
  return over > 2 ? over : 0;
};

export const pregnancyInfo = (c) => {
  const lmp = c.lmp || c.starts[c.starts.length - 1];
  if (!lmp) return null;
  const days = differenceInCalendarDays(new Date(), parseISO(lmp));
  if (days < 0) return null;
  const weeks = Math.floor(days / 7);
  const trimester = weeks < 13 ? "First" : weeks < 27 ? "Second" : "Third";
  const due = addDays(parseISO(lmp), 280);
  return { lmp, weeks, extraDays: days % 7, trimester, due: format(due, "EEE d MMM yyyy"), dueIso: format(due, "yyyy-MM-dd"), daysToGo: differenceInCalendarDays(due, new Date()) };
};

const mostCommon = (arr) => {
  const counts = arr.reduce((m, x) => ({ ...m, [x]: (m[x] ?? 0) + 1 }), {});
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
};

// Groups logged symptoms by the cycle day/phase they fell on
export const symptomInsights = (c) => {
  const hits = {};
  Object.entries(c.logs ?? {}).forEach(([iso, log]) => {
    const info = infoForDate(c, parseISO(iso));
    if (!info) return;
    (log.symptoms ?? []).forEach((s) => {
      hits[s] = hits[s] ?? [];
      hits[s].push(info);
    });
  });
  return Object.entries(hits)
    .map(([name, infos]) => {
      const days = infos.map((i) => i.day).sort((a, b) => a - b);
      const lo = days[Math.floor((days.length - 1) * 0.25)];
      const hi = days[Math.ceil((days.length - 1) * 0.75)];
      return { name, count: infos.length, phase: mostCommon(infos.map((i) => i.phase)), lo, hi };
    })
    .sort((a, b) => b.count - a.count);
};

export const useTodayPhase = () => {
  const { cycle } = useCycle();
  return cycle.pregnant ? null : todaySummary(cycle);
};

export const todaySummary = (c) => {
  const info = infoForDate(c, new Date());
  if (!info) return null;
  const nextStart = addDays(new Date(), info.len - info.day + 1);
  return { ...info, nextPeriod: format(nextStart, "EEE d MMM"), daysToNext: info.len - info.day + 1 };
};
